// E-mail při ukončení a neuzavření spolupráce (zadání 5, Kristina 28. 10. 2026; mockupy schválil Láďa
// 1. 11. 2026: 5A okno „Jak spolupráce končí?“, 5B rozlučkový e-mail, 5C neuzavřeno po zkušebním).
// E-mail se nikdy neposílá sám, vždy rozhodne člověk v náhledu, kde jde text i předmět upravit.
// Server: /api/rozlouceni (texty v netlify/functions/_lib/rozlouceni.cjs), ukončení přes /api/klient-edit.
import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, Frown, Mail, Send, Smile, X, HeartHandshake } from "lucide-react";
import { Modal, HlavickaOkna } from "./ui.jsx";
import { toast } from "./Toast.jsx";
import { apiFetch } from "../lib/api.js";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import UkonceniVolby from "./UkonceniVolby.jsx";
import { Pil } from "./sales/salesUi.jsx";

async function api(url, opt) {
  const r = await apiFetch(url, opt);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Chyba ${r.status}`);
  return j;
}
const dmr = (iso) => { if (!iso) return ""; const d = new Date(iso); return d.toLocaleDateString("cs-CZ", { timeZone: "Europe/Prague", day: "numeric", month: "numeric", year: "numeric" }); };
const krestni = (s) => String(s || "").trim().split(/\s+/)[0] || "";

/** Zaškrtávátko jako v mockupu (celý řádek je klikací). */
function Zaskrtnuti({ zapnuto, onZmena, nadpis, popis, disabled }) {
  return (
    <button type="button" role="checkbox" aria-checked={zapnuto} disabled={disabled} onClick={() => onZmena(!zapnuto)}
      className="flex w-full items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left disabled:opacity-60">
      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${zapnuto ? "bg-brand text-white" : "border-2 border-slate-300"}`}>{zapnuto && <Check className="h-3.5 w-3.5" strokeWidth={3} />}</span>
      <span className="min-w-0"><span className="block text-sm font-semibold text-slate-900">{nadpis}</span>{popis && <span className="block text-xs text-slate-500">{popis}</span>}</span>
    </button>
  );
}

function Dlazdice({ zapnuto, ton, ikona: Ikona, nadpis, popis, onClick }) {
  const ok = ton === "ok";
  return (
    <button type="button" aria-pressed={zapnuto} onClick={onClick}
      className={`flex min-h-[64px] w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition ${zapnuto ? (ok ? "border-2 border-emerald-500 bg-emerald-50" : "border-2 border-rose-400 bg-rose-50") : "border border-slate-200 bg-white hover:border-slate-300"}`}>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${zapnuto ? (ok ? "bg-emerald-600 text-white" : "bg-rose-500 text-white") : "bg-slate-100 text-slate-500"}`}><Ikona className="h-5 w-5" /></span>
      <span className="min-w-0"><span className="block text-[15px] font-bold text-slate-900">{nadpis}</span><span className="block text-xs text-slate-500">{popis}</span></span>
    </button>
  );
}

/** Patka okna: na počítači vedle sebe (zrušit vlevo), na telefonu pod sebou přes celou šířku (hlavní nahoře). */
function Patka({ hlavni, vedlejsi, zrusit }) {
  const uzka = useUzkaObrazovka();
  const polozky = uzka ? [hlavni, vedlejsi, zrusit] : [zrusit, vedlejsi, hlavni];
  return <div className={`flex w-full gap-2 ${uzka ? "flex-col" : "flex-row justify-end"}`}>{polozky.filter(Boolean).map((p, i) => <React.Fragment key={i}>{p}</React.Fragment>)}</div>;
}

export const VYCHOZI_KONEC = { typ: "v_dobrem", spokojena: true, blacklist: true };

/** Chyba výběru v okně ukončení (doplňuje chybaUkonceni z UkonceniVolby). */
export function chybaKonce(v) {
  if (!["v_dobrem", "ne_v_dobrem"].includes(v?.typ)) return "Vyber, jak spolupráce končí.";
  return "";
}

/**
 * 5A: okno „Změnit stav na Ukončeno?“ s volbou, jak spolupráce končí.
 * value = ukonceniInfo (duvod, poznamka, sales + typ, spokojena, blacklist).
 * onPotvrdit(sEmailem) zavolá karta, uložení proběhne až tlačítkem Uložit jako dřív.
 */
export function UkonceniDialog({ open, klientJmeno, popis, value, onChange, klientId, onPotvrdit, onZrusit }) {
  if (!open) return null;
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const dobre = v.typ !== "ne_v_dobrem";
  const zvolTyp = (typ) => {
    if (typ === "ne_v_dobrem") set({ typ, sales: "nevolat", blacklist: v.blacklist !== false });
    else set({ typ, sales: v.sales === "nevolat" ? "za3mesice" : v.sales });
  };
  const header = (
    <div className="flex items-start gap-3">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600"><AlertTriangle className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="text-lg font-bold leading-tight text-slate-900">Změnit stav na Ukončeno?</div>
      </div>
      <button type="button" onClick={onZrusit} className="btn-ikona" aria-label="Zavřít" title="Zavřít"><X className="h-5 w-5" /></button>
    </div>
  );
  const zrusit = <button type="button" className="btn-secondary" onClick={onZrusit}>Zrušit</button>;
  const footer = dobre
    ? <Patka zrusit={zrusit}
        vedlejsi={<button type="button" className="btn-secondary" onClick={() => onPotvrdit(false)}>Ukončit bez emailu</button>}
        hlavni={<button type="button" className="btn-danger" onClick={() => onPotvrdit(true)}>Ukončit a napsat email</button>} />
    : <Patka zrusit={zrusit} hlavni={<button type="button" className="btn-danger" onClick={() => onPotvrdit(false)}>Změnit na Ukončeno</button>} />;
  return (
    <Modal open={open} onClose={onZrusit} size="md" header={header} footer={footer}>
      <div className="space-y-4">
        <p className="text-[13px] text-slate-600"><strong>{klientJmeno}</strong>: {popis} Změna se uloží až tlačítkem Uložit.</p>
        <div>
          <div className="mb-2 text-sm font-bold text-slate-900">Jak spolupráce končí?</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Dlazdice zapnuto={dobre} ton="ok" ikona={Smile} nadpis="V dobrém" popis="nabídne rozlučkový email" onClick={() => zvolTyp("v_dobrem")} />
            <Dlazdice zapnuto={!dobre} ton="problem" ikona={Frown} nadpis="Ne v dobrém" popis="bez emailu, nevolat" onClick={() => zvolTyp("ne_v_dobrem")} />
          </div>
        </div>
        <UkonceniVolby value={v} onChange={onChange} klientId={klientId} />
        {dobre ? (
          <div>
            <div className="mb-2 text-sm font-bold text-slate-900">Rozlučkový email</div>
            <Zaskrtnuti zapnuto={v.spokojena !== false} onZmena={(x) => set({ spokojena: x })} nadpis="Klientka byla spokojená" popis="do emailu přidat prosbu o Google hodnocení" />
          </div>
        ) : (
          <div>
            <div className="mb-2 text-sm font-bold text-slate-900">Ne v dobrém</div>
            <Zaskrtnuti zapnuto={v.blacklist !== false} onZmena={(x) => set({ blacklist: x })} nadpis="Přidat na blacklist ve SmartEmailingu" popis="přestanou jí chodit hromadné emaily" />
            <div className="mt-2 text-xs text-slate-500">
              Email se nenabízí. {v.sales === "nevolat" ? "Sales je „Už jí nevolat“, takže nebude ani v seznamu pro hodnocení výživy." : "Když má být i mimo seznam pro hodnocení výživy, vyber u Sales „Už jí nevolat“."}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * 5B a 5C: náhled e-mailu, předmět i text jdou upravit, odesílá se z klienti@zateckal.cz.
 * druh: "rozlucka" (klientId, spokojena) | "neuzavreno" (leadId).
 */
export function EmailNahledDialog({ open, druh, klientId = null, leadId = null, spokojena = true, popis = "", onZavrit, onOdeslano }) {
  const [n, setN] = useState(null);
  const [predmet, setPredmet] = useState("");
  const [chyba, setChyba] = useState("");
  const [busy, setBusy] = useState(false);
  const telo = useRef(null);
  const rozlucka = druh === "rozlucka";

  useEffect(() => {
    if (!open) return undefined;
    let zivy = true;
    setN(null); setChyba(""); setBusy(false);
    const q = rozlucka ? `klient_id=${encodeURIComponent(klientId || "")}&spokojena=${spokojena ? 1 : 0}` : `lead_id=${encodeURIComponent(leadId || "")}`;
    api(`/api/rozlouceni?akce=nahled&druh=${druh}&${q}`)
      .then((d) => { if (zivy) { setN(d); setPredmet(d.subject || ""); } })
      .catch((e) => zivy && setChyba(e.message));
    return () => { zivy = false; };
  }, [open, druh, klientId, leadId, spokojena, rozlucka]);
  // Obsah se do upravitelného pole vloží jednou, další psaní už React nepřekresluje (kurzor neskáče).
  useEffect(() => { if (n && telo.current) telo.current.innerHTML = n.html || ""; }, [n]);

  if (!open) return null;
  const poslat = async () => {
    if (!n || busy) return;
    if (!predmet.trim()) { setChyba("Vyplň předmět."); return; }
    setBusy(true); setChyba("");
    try {
      const r = await api("/api/rozlouceni", {
        method: "POST",
        body: JSON.stringify({ akce: "poslat", druh, ...(rozlucka ? { klient_id: klientId, spokojena } : { lead_id: leadId }), subject: predmet, html: telo.current ? telo.current.innerHTML : n.html }),
      });
      toast(rozlucka ? "Rozlučkový email odešel a je zapsaný u klientky." : "Email odešel a je zapsaný v historii kontaktu.", "success");
      onOdeslano?.(r);
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  const bezEmailu = n && !n.komu;
  const footer = (
    <>
      {chyba && <div className="basis-full rounded-xl bg-rose-50 px-3 py-2 text-[13px] font-semibold text-rose-800">{chyba}</div>}
      <Patka
        zrusit={<button type="button" className="btn-secondary" onClick={onZavrit} disabled={busy}>{rozlucka ? "Zrušit" : "Neposílat"}</button>}
        hlavni={<button type="button" className="btn-primary" onClick={poslat} disabled={busy || !n || bezEmailu} aria-busy={busy}><Send className="h-4 w-4" />{busy ? "Odesílám…" : rozlucka ? "Odeslat klientce" : "Poslat email"}</button>} />
    </>
  );
  return (
    <Modal open={open} onClose={() => !busy && onZavrit?.()} size="md" plnaHlavicka footer={footer}
      header={<HlavickaOkna ikona={Mail} nazev={rozlucka ? "Rozlučkový email" : "Neuzavřeno"} popis={popis || n?.jmeno || ""} onClose={() => !busy && onZavrit?.()} />}>
      {!n && !chyba && <div className="py-6 text-center text-sm text-slate-500">Připravuji náhled…</div>}
      {n && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            <Pil ton="znacka" casti={`Komu: ${n.komu || "chybí e-mail"}`} />
            <Pil ton="neutralni" casti={`Od: ${n.od}`} />
          </div>
          {bezEmailu && <div className="rounded-xl bg-amber-50 px-3 py-2 text-[13px] text-amber-800">{rozlucka ? "Klientka" : "Kontakt"} nemá vyplněný e-mail, nejdřív ho doplň v kartě.</div>}
          <label className="block">
            <span className="label">Předmět</span>
            <input className="field w-full font-semibold" value={predmet} maxLength={200} onChange={(e) => setPredmet(e.target.value)} />
          </label>
          <div>
            <span className="label">Text emailu (jde upravit)</span>
            <div ref={telo} contentEditable={!busy} suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label="Text emailu"
              className="rounded-2xl border border-[#d6cfe0] bg-[#FCFBFE] p-4 text-[15px] leading-relaxed text-[#1f1f1f] outline-none focus:border-brand [overflow-wrap:anywhere]" />
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Karta klientky: jak spolupráce skončila, kdo a kdy, e-mail a blacklist. Rozlučku jde poslat i později. */
export function KonecSpolupraceSection({ klientId, klientName, stav, readOnly = false, obnova = 0 }) {
  const [d, setD] = useState(null);
  const [spokojena, setSpokojena] = useState(true);
  const [okno, setOkno] = useState(false);
  const nacti = React.useCallback(() => {
    if (!klientId) return;
    api(`/api/rozlouceni?akce=stav&klient_id=${encodeURIComponent(klientId)}`)
      .then((x) => { setD(x); setSpokojena(x.spokojena !== false); })
      .catch(() => setD(null));
  }, [klientId]);
  useEffect(() => { nacti(); }, [nacti, stav, obnova]);
  if (!d || d.stav !== "Ukončeno") return null;
  const bl = d.blacklist;
  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <div className="mb-3 flex items-center gap-2">
        <HeartHandshake className="h-4 w-4 text-brand" />
        <h4 className="text-sm font-semibold text-slate-800">Jak spolupráce skončila</h4>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {d.typ === "v_dobrem" && <Pil ton="ok" casti="V dobrém" />}
        {d.typ === "ne_v_dobrem" && <Pil ton="problem" casti="Ne v dobrém" />}
        {!d.typ && <Pil ton="neutralni" casti="Nezapsáno, jak skončila" />}
        {(d.kdo || d.kdy) && <Pil ton="neutralni" casti={["Ukončení", krestni(d.kdo), dmr(d.kdy)]} />}
        {d.email && <Pil ton="ok" ikona={Mail} casti={["Email odešel", dmr(d.email.kdy), krestni(d.email.kdo)]} />}
        {!d.email && d.typ !== "ne_v_dobrem" && <Pil ton="pozor" ikona={Mail} casti="Email neodešel" />}
        {d.nevolat && <Pil ton="pozor" casti="Nevolat" />}
        {bl && bl.ok && <Pil ton="neutralni" casti={["SmartEmailing blacklist", dmr(bl.kdy)]} />}
        {bl && bl.ok === false && <Pil ton="problem" casti="Blacklist se nepovedl" />}
      </div>
      {bl && bl.ok === false && bl.chyba && <div className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-[13px] text-rose-800">{bl.chyba} Na blacklist ji přidej ručně ve SmartEmailingu.</div>}
      {!readOnly && d.lze_poslat && (
        <div className="mt-3 space-y-2">
          <Zaskrtnuti zapnuto={spokojena} onZmena={setSpokojena} nadpis="Klientka byla spokojená" popis="do emailu přidat prosbu o Google hodnocení" />
          <button type="button" className="btn-primary w-full sm:w-auto" onClick={() => setOkno(true)}><Mail className="h-4 w-4" />Poslat rozlučkový email</button>
        </div>
      )}
      <EmailNahledDialog open={okno} druh="rozlucka" klientId={klientId} spokojena={spokojena} popis={klientName}
        onZavrit={() => setOkno(false)} onOdeslano={() => { setOkno(false); nacti(); }} />
    </div>
  );
}

/** Karta kontaktu v sales: e-mail s nabídkou bootcampu a Klubu po neuzavřeném zkušebním tréninku. */
export function NeuzavrenoEmailBlok({ lead, otevrit = false, onOtevreno, onZmena }) {
  const [d, setD] = useState(null);
  const [okno, setOkno] = useState(false);
  const nacti = React.useCallback(() => {
    if (!lead?.id) return;
    api(`/api/rozlouceni?akce=stav&lead_id=${encodeURIComponent(lead.id)}`).then(setD).catch(() => setD(null));
  }, [lead?.id]);
  useEffect(() => { nacti(); }, [nacti, lead?.stav]);
  // Hned po zápisu prohry se okno nabídne samo (zadání: „při nastavení stavu Neuzavřeno okno“).
  useEffect(() => { if (otevrit && d?.lze_poslat) { setOkno(true); onOtevreno?.(); } }, [otevrit, d]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d || d.stav !== "neuzavreno") return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Email po zkušebním</div>
      <div className="flex flex-wrap gap-1.5">
        {d.email ? <Pil ton="ok" ikona={Mail} casti={["Email odešel", dmr(d.email.kdy), krestni(d.email.kdo)]} />
          : <Pil ton="pozor" ikona={Mail} casti={d.komu ? "Email zatím neodešel" : "Chybí e-mail"} />}
      </div>
      {d.lze_poslat && (
        <button type="button" className="btn-primary mt-3 w-full" onClick={() => setOkno(true)}><Mail className="h-4 w-4" />Poslat email s nabídkou bootcampu a Klubu</button>
      )}
      <EmailNahledDialog open={okno} druh="neuzavreno" leadId={lead.id} popis={lead.jmeno}
        onZavrit={() => setOkno(false)} onOdeslano={() => { setOkno(false); nacti(); onZmena?.(); }} />
    </div>
  );
}
